/**
 * 图表数值展示格式纯函数。
 *
 * @see types.ts · constants.ts
 */

import { ChartType, type TChartType } from '@dbl/core/chart';
import { FieldType, type TFieldType } from '@dbl/core/dataSource';

import {
  CHART_TYPES_WITH_COUNT_UNIT_DEFAULT_ACCURACY,
  ChartTypeDefaultUnit,
} from './constants';
import {
  NumericalUnit,
  type IChartDisplayConfig,
  type IOldChartFieldDisplayData,
  type TNumericalUnit,
} from './types';

/**
 * 判断 fieldType 是否落在 allowed 集合内。
 *
 * 内联写 `[FieldType.String, FieldType.Date].includes(fieldType)` 时，TS 会把数组
 * 推断为窄字面量类型（如 `"string" | "date"[]`），而 fieldType 是完整 TFieldType，
 * includes 参数类型不兼容会报错。将 allowed 显式标注为 readonly TFieldType[] 后，
 * 调用处仍可写内联数组，无需 as 断言，也无需把仅本文件使用的集合抽到 constants。
 */
function isFieldTypeOneOf(
  fieldType: TFieldType | undefined,
  allowed: readonly TFieldType[],
): boolean {
  return fieldType != null && allowed.includes(fieldType);
}

/**
 * 旧图表字段显示配置兼容：补齐缺失的 accuracy / unit。
 *
 * @core DashboardLayout 各 TragCard 与多维 Analysis model 读存共用；
 *       放 ChartTragCard 内会导致 TableTragCard / BigNumberTragCard 各复制一份兼容逻辑。
 */
export function getOldChartDisplayConfig(
  data: IOldChartFieldDisplayData,
  chartType: TChartType,
): IChartDisplayConfig {
  const { column_type: fieldType, aggregate, accuracy, unit } = data;
  let newAccuracy = accuracy;
  let newUnit = unit;
  const isCountAggregate = aggregate === 'count' || aggregate === 'distinct';
  const hasUnit =
    fieldType === FieldType.Number ||
    fieldType === FieldType.Float ||
    (isFieldTypeOneOf(fieldType, [FieldType.String, FieldType.Date]) &&
      isCountAggregate);

  if (hasUnit && !unit) {
    const shouldApplyCountUnitAccuracy =
      CHART_TYPES_WITH_COUNT_UNIT_DEFAULT_ACCURACY.has(chartType);

    if (
      shouldApplyCountUnitAccuracy &&
      (fieldType === FieldType.Number ||
        (isFieldTypeOneOf(fieldType, [FieldType.String, FieldType.Date]) &&
          isCountAggregate))
    ) {
      newAccuracy = 2;
    }

    newUnit =
      chartType === ChartType.Map
        ? NumericalUnit.ORIGINAL
        : ChartTypeDefaultUnit[chartType];
  }

  return { accuracy: newAccuracy, unit: newUnit };
}

/**
 * 拖拽字段时按字段 / 图表类型给数值显示单位默认值。
 *
 * @core SortableDrag 持久化、BatchAddFieldPopover、FieldEditComponents 写入时共用。
 */
export function getDefaultNumberUnit({
  fieldType,
  chartType,
}: {
  fieldType: TFieldType;
  chartType: TChartType;
}): TNumericalUnit | undefined {
  if (
    isFieldTypeOneOf(fieldType, [
      FieldType.Percent,
      FieldType.String,
      FieldType.Date,
    ])
  ) {
    return undefined;
  }

  if (isFieldTypeOneOf(fieldType, [FieldType.Number, FieldType.Float])) {
    return NumericalUnit.ORIGINAL;
  }

  return ChartTypeDefaultUnit[chartType];
}
